/**
 * プリセットの書式（#217 から見積書・請求書の紙そのもの。複製すれば自分の書式の元）。
 *   ・#217（2026-10-19 射場さん「紙を1本に」）：プリセットも packages/paper の書式 JSON＋版 2 の描き手（engine.ts）で描く。
 *     形の正は #216 までの A4横の見積書の PDF（本番の Q-0006）＝流し込み型の紙を Chromium で描いて測った mm（docs/31 §15）。
 *     題字（明朝・字間）と二重線／左：宛名＋御中・挨拶文・御見積金額 ¥○－（税込）／中：頭の欄（空でも見出しと罫線）／
 *     右：番号・日付・ロゴ・左に二重の縦線の会社欄・社印は会社名に重ねる／明細表は中身で行が伸び、小計・消費税（税率ごと）・合計（税込）は表の中、
 *     備考は表の下に横幅いっぱいの枠（高さは中身）、内訳は内訳ごとの紙、ページの下に帳票名とページ番号
 *   ・標準（A4縦）・標準（A4横 表紙＋内訳）が元。シンプル・クラシック・ボックス・数字大きめは、その JSON の色・罫線・行の高さ・文字の大きさだけを
 *     プリセットのテーマ（contract の PAPER_PRESETS の theme）で差し替えたもの（look2）。備考はどれも横幅いっぱい。
 *     標準（A4横 表紙＋内訳(2)）（#221）は列の幅だけ＝名称と規格を同じ幅に（EVEN_NAME_SPEC）
 *   ・詰める組（stack）：宛名（L）・頭の欄（横は H）・番号と発行者欄（R）。空の項目・無いロゴは詰め、何行にもなる項目の分だけ下を押す
 *   ・領収書は #217 の対象外：#216 の複製元（realLayout）のまま＝前の描き手（版 1）で描く
 *   ・消せない部品（locked）は PAPER_REQUIRED_PARTS（DB の template_part_rules と同じ）。表の中の合計（table.foot）は 合計・税率ごとの内訳 に数える
 */
import {
  DEFAULT_LABELS, DEFAULT_LINE_LABELS, PAPER_BIND_PART, PAPER_SIZES, PaperEl, PaperLayout, PaperTheme, paperBindLabel, paperPreset, requiredPartsFor,
  PaperTable, type Labels, type PaperBind, type PaperKind, type PaperOrient, type PaperPresetKey,
} from "@genbaryoku/contract";

type Extra = Record<string, unknown>;

let seq = 0;
const nid = (): string => `e${++seq}`;

function base(type: PaperEl["type"], name: string, x: number, y: number, w: number, h: number, frame: boolean, extra: Extra): Extra {
  return { id: nid(), type, name, on: true, frame, locked: false, x, y, w, h, ...extra };
}
const T = (name: string, x: number, y: number, w: number, h: number, text: string, ex: Extra = {}): Extra =>
  base("text", name, x, y, w, h, true, { text, font: "sans", size: 9, bold: false, align: "left", valign: "middle", color: "#55534C", ...ex });
const F = (name: string, x: number, y: number, w: number, h: number, bind: PaperBind, ex: Extra = {}): Extra =>
  base("field", name, x, y, w, h, false, { bind, font: "sans", size: 9.5, bold: false, align: "left", valign: "middle", color: "#1A1A18", ...ex });
const LN = (name: string, x: number, y: number, w: number, ex: Extra = {}): Extra =>
  base("line", name, x, y, w, 0, true, { dir: "h", style: "solid", lw: 0.25, color: "#C8C5BC", ...ex });
const BX = (name: string, x: number, y: number, w: number, h: number, ex: Extra = {}): Extra =>
  base("box", name, x, y, w, h, true, { style: "solid", lw: 0.5, color: "#8C8880", fill: "", ...ex });

/** 領収書の試作 v2 の形（046。#216 からは使わない＝領収書の複製元は realEls。形の記録として残す） */
export function boxReceiptEls(): Extra[] {
  const els: Extra[] = [];
  const cw = 180;
  const R = 120;
  els.push(T("題字", 15, 14, cw, 13, "領 収 書", { font: "serif", size: 24, bold: true, align: "center", color: "#1A1A18", part: "title", locked: true }));
  els.push(T("番号の見出し", 140, 30, 20, 5.5, "領収番号", { size: 8.5, group: "docno" }));
  els.push(F("領収番号", 161, 30, 34, 5.5, "docNo", { size: 9.5, group: "docno", locked: true }));
  els.push(T("領収日の見出し", 140, 36, 20, 5.5, "領収日", { size: 8.5, group: "docdate" }));
  els.push(F("領収日", 161, 36, 34, 5.5, "docDate", { size: 9.5, group: "docdate", locked: true }));
  els.push(F("宛名", 15, 31, 95, 9, "toName", { size: 14, locked: true, group: "to" }));
  els.push(LN("宛名の下線", 15, 40.5, 95, { lw: 0.5, color: "#1A1A18", group: "to" }));
  els.push(F("宛名の担当者", 15, 41.5, 95, 5, "toSub", { size: 8.5, color: "#55534C", group: "to" }));
  els.push(T("挨拶文", 15, 50, 100, 6, "下記のとおり領収いたしました。", { size: 9.5, color: "#1A1A18" }));
  els.push(BX("領収金額の枠", 15, 58, 100, 13, { style: "double", lw: 0.5, color: "#8C8880", fill: "#EFEEE9", group: "grand" }));
  els.push(T("領収金額の見出し", 18, 58, 30, 13, "領収金額", { size: 10.5, bold: true, color: "#123C63", group: "grand" }));
  els.push(F("領収金額（税込）", 48, 58, 52, 13, "total", { font: "serif", size: 19, bold: true, align: "right", color: "#123C63", locked: true, group: "grand" }));
  els.push(T("（税込）", 100, 58, 14, 13, "（税込）", { size: 8, color: "#55534C", group: "grand" }));
  els.push(T("但しの見出し", 15, 74, 12, 6, "但し", { size: 9.5, bold: true, color: "#1A1A18", group: "proviso" }));
  els.push(F("但し書き", 27, 74, 88, 6, "proviso", { size: 10, group: "proviso" }));
  const rows: Array<[string, PaperBind]> = [["件名", "projectName"], ["請求書", "invoiceNo"]];
  rows.forEach(([lb, b], i) => {
    const y = 82 + i * 6;
    const g = `pj${i}`;
    els.push(T(`${lb}の見出し`, 15, y, 21, 6, lb, { size: 8.5, group: g }));
    els.push(F(lb, 36, y, 79, 6, b, { size: 9, group: g }));
    els.push(LN(`${lb}の下線`, 15, y + 6, 100, { lw: 0.1, color: "#C8C5BC", group: g }));
  });
  const cy = 46;
  els.push(F("自社・会社名", R, cy, 60, 7, "coName", { size: 12, bold: true, locked: true, group: "co" }));
  els.push(F("自社・住所", R, cy + 7.5, 62, 9, "coAddr", { size: 8.5, group: "co", valign: "top" }));
  els.push(F("自社・電話", R, cy + 16.5, 62, 5, "coTel", { size: 8.5, group: "co" }));
  els.push(F("自社・登録番号", R, cy + 21.5, 62, 5, "coReg", { size: 8.5, locked: true, group: "co" }));
  els.push(base("image", "会社のロゴ", R, 30, 18, 12, false, { asset: "company:logo", opacity: 1, keep: true, guide: false }));
  els.push(base("image", "社印", R + 44, cy + 26, 18, 18, false, { asset: "company:seal", opacity: 0.9, keep: true, guide: false }));
  const gx = cw - 65 + 15;
  els.push(BX("合計欄（明細の下）の枠", gx, 0, 65, 21, { dock: "table", dy: 0, style: "double", lw: 0.5, color: "#8C8880", group: "sum" }));
  const sums: Array<[string, PaperBind, number, boolean]> = [["小計", "subtotal", 0, false], ["消費税", "tax", 7, false], ["合計", "total", 14, true]];
  sums.forEach(([lb, b, d, bd]) => {
    els.push(T(`${lb}（明細の下）`, gx, 0, 30, 7, lb, { dock: "table", dy: d, size: 9, align: "right", color: "#1A1A18", group: "sum" }));
    els.push(F(`${lb}の金額`, gx + 31, 0, 32, 7, b, { dock: "table", dy: d, size: bd ? 10.5 : 9.5, bold: bd, align: "right", group: "sum" }));
    if (d) els.push(LN(`${lb}の上の線`, gx, 0, 65, { dock: "table", dy: d, lw: 0.1, color: "#C8C5BC", group: "sum" }));
  });
  els.push(F("税率ごとの内訳", 15, 0, 100, 10, "taxByRate", { dock: "table", dy: 14, size: 8, color: "#55534C", valign: "top", locked: true }));
  els.push(F("ページ番号", 105 - 15, 297 - 11, 30, 6, "pageNo", { size: 8.5, align: "center", color: "#55534C" }));
  return els;
}

/**
 * 見出しの言葉（会社の言葉。#142 段6 No.32）。outline（工事概要）は #216 から（省けば現場力の言葉）。
 * lineName・lineSpec・lineNote は明細表の列の見出し（#222、docs/22 §6。省けば 名称／規格／備考）
 */
type HeadLabels = Pick<Labels, "subject" | "site" | "period"> & Partial<Pick<Labels, "outline" | "lineName" | "lineSpec" | "lineNote">>;
/** 明細表の列の見出し（No.・数量・単位・単価・金額は固定、名称・規格・備考は会社の言葉） */
const colLabels = (l: HeadLabels) =>
  ({ no: "No.", name: l.lineName ?? DEFAULT_LINE_LABELS.lineName, spec: l.lineSpec ?? DEFAULT_LINE_LABELS.lineSpec, qty: "数量", unit: "単位", price: "単価", amount: "金額", notes: l.lineNote ?? DEFAULT_LINE_LABELS.lineNote }) as const;

// ---- 本物の紙（流し込み型）に合わせた複製元（#216） ----
/** 本物の紙の線の太さ（mm）。1px＝0.26・1.5px＝0.4・2px＝0.53・3px の二重＝0.8・0.4pt＝0.14・0.6pt＝0.21 */
const PX1 = 0.26, PX15 = 0.4, PX2 = 0.53, DBL = 0.8, PT04 = 0.14, PT06 = 0.21;
const INK = "#1A1A18";
/** 本物の紙の薄い文字（#444） */
const SUB = "#444444";
const STD = PaperTheme.parse({});
const up = (c: string): string => c.toUpperCase();
const r1 = (v: number): number => Math.round(v * 10) / 10;

type Rule = { style: "none" | "solid" | "double"; w: number; color: string };
/** テーマ（流し込み型の themeCss と同じ差分）→ 自分の書式の線・色・地色・行間 */
type Look = {
  title: { font: "serif" | "sans"; color: string; rule: Rule | null };
  amount: { color: string; frame: "rule" | "double" | "fill"; fill: string; rule: string };
  toRule: { lw: number; color: string };
  issuerRule: Rule;
  table: { outer: Rule; head: Rule; h: Rule; v: Rule; headFill: string; zebra: string; totalFill: string; sumTop: Rule };
  numLarge: boolean;
  /** 明細の行の高さ（9pt×1.45＋上下の余白＋線。標準 7.3mm） */
  pitch: number;
};

function lookOf(theme: PaperTheme): Look {
  const t = { ...STD, ...theme };
  const stdRule = t.rule === STD.rule;
  const rule = stdRule ? INK : up(t.rule);
  const accent = t.accent === STD.accent ? INK : up(t.accent);
  // シンプル（列の間の線なし）は 罫線を薄く・金額の枠は地色だけ（themeCss の !vlines）
  const simple = !t.vlines;
  const grid = t.gridColor ? up(t.gridColor) : "";
  const fs = t.numLarge ? 10 : 9;
  return {
    title: { font: t.titleFont, color: accent, rule: t.titleBorder === "none" ? null : t.titleBorder === "solid" ? { style: "solid", w: PX15, color: rule } : { style: "double", w: DBL, color: rule } },
    amount: {
      color: t.amountColor ? up(t.amountColor) : accent,
      frame: simple ? "fill" : t.amountBorder === "double" ? "double" : "rule",
      fill: simple ? up(t.zebra || "#F4F3EF") : t.amountFill ? up(t.amountFill) : "",
      rule,
    },
    toRule: { lw: simple ? PX1 : PX15, color: rule },
    issuerRule: simple ? { style: "solid", w: PX15, color: rule } : { style: "double", w: DBL, color: rule },
    table: {
      outer: simple ? { style: "solid", w: PT06, color: rule } : t.titleBorder === "double" && !stdRule ? { style: "double", w: DBL, color: rule } : { style: "solid", w: PX1, color: rule },
      head: { style: "solid", w: simple ? PX15 : PX2, color: simple ? INK : rule },
      h: simple ? { style: "solid", w: PT04, color: "#C8C5BC" } : { style: "solid", w: PX1, color: grid || INK },
      v: simple ? { style: "none", w: PT04, color: "#C8C5BC" } : { style: "solid", w: PT04, color: grid || "#8A8A8A" },
      headFill: up(t.headFill),
      zebra: up(t.zebra),
      totalFill: up(t.headFill === STD.headFill ? "#F0F0F0" : t.headFill || t.zebra || "#F4F3EF"),
      sumTop: { style: "solid", w: simple ? PX15 : PX2, color: simple ? INK : rule },
    },
    numLarge: t.numLarge,
    pitch: r1(fs * 1.45 * 0.3528 + 2 * t.rowPad + PX1),
  };
}

/** 明細表の左端（本物の紙の余白 15mm） */
const x0Of = (_o: PaperOrient): number => 15;

type ColDef = [PaperTable["cols"][number]["id"], number, number];
/** 列（表の左端からの x・幅 mm）。本物の紙の列の幅をもとに、数字の列は桁が切れないよう少し広く（単価 15 → 22・金額 24 → 28・数量 12 → 14） */
function realCols(orient: PaperOrient, numLarge: boolean): ColDef[] {
  const widths: Array<[ColDef[0], number]> = numLarge
    ? orient === "landscape"
      ? [["no", 8], ["name", 113], ["spec", 36], ["qty", 16], ["unit", 10], ["price", 24], ["amount", 30], ["notes", 30]]
      : [["no", 8], ["name", 37], ["spec", 30], ["qty", 16], ["unit", 10], ["price", 24], ["amount", 30], ["notes", 25]]
    : orient === "landscape"
      ? [["no", 8], ["name", 90], ["spec", 45], ["qty", 14], ["unit", 10], ["price", 22], ["amount", 28], ["notes", 50]]
      : [["no", 8], ["name", 45], ["spec", 33], ["qty", 14], ["unit", 10], ["price", 22], ["amount", 28], ["notes", 20]];
  let x = 0;
  return widths.map(([id, w]) => {
    const c: ColDef = [id, x, w];
    x += w;
    return c;
  });
}

function realTable(orient: PaperOrient, look: Look, y: number, rows: number, labels: HeadLabels = DEFAULT_LABELS): PaperTable {
  const L = orient === "landscape";
  const fs = look.numLarge ? 10 : 9;
  const num = (id: string) => ["qty", "price", "amount"].includes(id);
  const lbl = colLabels(labels);
  return {
    x: 15, y, w: L ? 267 : 180,
    header: { on: true, h: r1(look.pitch + 0.1), size: fs, bold: false, fill: look.table.headFill, color: INK },
    rows, pitch: look.pitch, size: fs, color: INK, zebra: look.table.zebra, restText: "以下余白",
    rules: { outer: look.table.outer, head: look.table.head, h: look.table.h, v: look.table.v },
    cols: realCols(orient, look.numLarge).map(([id, x, w]) => ({
      id, label: lbl[id], on: true, x, w,
      size: id === "notes" ? fs - 0.5 : num(id) && look.numLarge ? 10.5 : fs,
      align: id === "no" ? "center" : num(id) ? "right" : "left",
      labelAlign: "center",
    })),
  };
}

const TITLE: Record<PaperKind, string> = { quote: "御 見 積 書", invoice: "御 請 求 書", receipt: "領 収 書", order: "注 文 書" };
const GREET: Record<PaperKind, string> = { quote: "下記のとおり御見積り申し上げます。", invoice: "下記のとおり御請求申し上げます。", receipt: "下記のとおり領収いたしました。", order: "下記のとおり注文いたします。" };
const AMOUNT_LABEL: Record<PaperKind, string> = { quote: "御 見 積 金 額", invoice: "御 請 求 金 額", receipt: "領 収 金 額", order: "注 文 金 額" };
const NO_NAME: Record<PaperKind, [string, string]> = { quote: ["見積番号", "見積日"], invoice: ["請求番号", "請求日"], receipt: ["領収番号", "領収日"], order: ["番号", "発行日"] };

/**
 * 本物の紙と同じ見た目の部品（#216）。位置は本物の紙を Chromium で描いて測った mm（縦：紙の中の左上＝15, 14）。
 *   上：題字（明朝 22pt・中央）と二重線 → 左：宛名・下線・宛名の担当者・挨拶文・金額の枠（上下の線、¥ と － つき）・頭の欄
 *   （縦は金額の下、横は金額の右の列）／右：ロゴ・No.・日付・発行者（左に二重線・社印を重ねる）
 *   明細表のすぐ下に 小計／消費税／合計（税込） の行（表の幅で線を引く＝表の中の行に見える）→ 税率ごとの内訳 → 備考の枠（見積書・請求書）
 */
function realLayout(kind: PaperKind, orient: PaperOrient, look: Look, labels: HeadLabels): { els: Extra[]; table: PaperTable } {
  const L = orient === "landscape";
  const W = L ? 267 : 180;
  const R = L ? 210 : 123; // 右の列（発行者）の左
  // 宛名・金額の枠の幅。横は頭の欄に幅を残すため 92mm（金額は 17pt＝10桁まで入る。縦は 18pt）
  const leftW = L ? 92 : 100;
  const amtW = L ? 52 : 60;
  const els: Extra[] = [];
  // 題字と二重線
  els.push(T("題字", 15, 14, W, 11.5, TITLE[kind], { font: look.title.font, size: 22, bold: true, align: "center", color: look.title.color, part: "title", locked: true }));
  if (look.title.rule) els.push(LN("題字の下線", 15, 27.2, W, { style: look.title.rule.style, lw: look.title.rule.w, color: look.title.rule.color }));
  // 右上：ロゴ（番号の左）・No.・日付
  const [noName, dateName] = NO_NAME[kind];
  els.push(base("image", "会社のロゴ", R, 34, 30, 12, false, { asset: "company:logo", opacity: 1, keep: true, guide: false }));
  els.push(F(noName, R + 32, 34, 40, 6.2, "docNo", { font: "serif", size: 12, bold: true, align: "right", color: INK, prefix: "No. ", group: "docno", locked: true }));
  els.push(F(dateName, R + 32, 40.4, 40, 4.8, "docDate", { size: 10, align: "right", color: INK, group: "docdate", locked: true }));
  // 右：発行者（左に二重線）と社印
  els.push(LN("自社の左の線", R, 48, 0, { dir: "v", h: 38.3, style: look.issuerRule.style, lw: look.issuerRule.w, color: look.issuerRule.color, group: "co" }));
  const cx = R + 3.8;
  els.push(F("自社・会社名", cx, 48, 68, 9.5, "coName", { size: 12, bold: true, valign: "top", color: INK, locked: true, group: "co" }));
  els.push(F("自社・代表者名", cx, 57.5, 68, 4.8, "coRep", { size: 9, color: INK, group: "co" }));
  els.push(F("自社・住所", cx, 62.3, 68, 14.4, "coAddr", { size: 9, valign: "top", color: INK, group: "co" }));
  els.push(F("自社・電話", cx, 76.7, 68, 4.8, "coTel", { size: 9, color: INK, group: "co" }));
  els.push(F("自社・登録番号", cx, 81.5, 68, 4.8, "coReg", { size: 9, color: INK, locked: kind !== "quote", group: "co" }));
  els.push(base("image", "社印", R + 55, 45.5, 17, 17, false, { asset: "company:seal", opacity: 0.9, keep: true, guide: false }));
  // 左：宛名・挨拶文・金額の枠
  els.push(F("宛名", 15, 34, leftW, 8.5, "toName", { size: 14, bold: true, color: INK, locked: true, group: "to" }));
  els.push(LN("宛名の下線", 15, 42.8, Math.min(90, leftW), { lw: look.toRule.lw, color: look.toRule.color, group: "to" }));
  els.push(F("宛名の担当者", 15, 43.4, leftW, 5.5, "toSub", { size: 10.5, color: INK, group: "to" }));
  els.push(T("挨拶文", 15, 50.4, leftW, 5, GREET[kind], { size: 9.5, color: INK }));
  const ay = 57;
  const ah = 12.7;
  const a = look.amount;
  if (a.frame === "rule") {
    if (a.fill) els.push(BX("合計欄の枠", 15, ay, leftW, ah, { style: "none", fill: a.fill, group: "grand" }));
    els.push(LN("金額の枠の上の線", 15, ay, leftW, { lw: PX15, color: a.rule, group: "grand" }));
    els.push(LN("金額の枠の下の線", 15, ay + ah, leftW, { lw: PX15, color: a.rule, group: "grand" }));
  } else {
    els.push(BX("合計欄の枠", 15, ay, leftW, ah, a.frame === "double" ? { style: "double", lw: DBL, color: a.rule, fill: a.fill, group: "grand" } : { style: "none", fill: a.fill, group: "grand" }));
  }
  const pad = a.frame === "rule" ? 1 : 3;
  els.push(T("合計欄の見出し", 15 + pad, ay, 24, ah, AMOUNT_LABEL[kind], { size: 10, color: a.color, group: "grand" }));
  els.push(F("合計（税込）", 15 + leftW - 15 - amtW, ay, amtW, ah, "total", { font: "serif", size: L ? 17 : 18, bold: true, align: "right", color: a.color, prefix: "¥", suffix: "－", locked: true, group: "grand" }));
  els.push(T("（税込）", 15 + leftW - 15, ay, 15, ah, "（税込）", { size: 8.5, color: SUB, group: "grand" }));
  // 頭の欄（縦は金額の枠の下、横は金額の右の列）
  let hy = L ? 34 : ay + ah + 3.1;
  const hx = L ? 15 + leftW + 8 : 15;
  const hw = L ? R - 8 - hx : leftW;
  if (kind === "receipt") {
    // 但し書き（本物の紙の .tadashi：太字の「但し」＋本文 10.5pt）
    els.push(T("但しの見出し", 15, ay + ah + 2, 12, 6.5, "但し", { size: 10.5, bold: true, color: INK, group: "proviso" }));
    els.push(F("但し書き", 27, ay + ah + 2, leftW - 12, 6.5, "proviso", { size: 10.5, color: INK, group: "proviso" }));
    hy = ay + ah + 9.1;
  }
  const heads: Array<[string, PaperBind, number]> =
    kind === "quote"
      ? [[labels.subject, "projectName", 6.7], [labels.site, "site", 6.7], [labels.period, "period", 6.7], [labels.outline ?? DEFAULT_LABELS.outline, "outline", 6.7], ["有効期限", "valid", 6.7], ["担当", "staff", 6.7]]
      : kind === "invoice"
        ? [["件名", "projectName", 6.7], [labels.site, "site", 6.7], [labels.period, "period", 6.7], ["お支払期限", "due", 6.7], ["振込先", "bank", 11.6]]
        : [["件名", "projectName", 6.7], ["請求書", "invoiceNo", 6.7]];
  heads.forEach(([lb, b, h], i) => {
    const g = `pj${i}`;
    els.push(T(`${lb}の見出し`, hx, hy, 20, h, lb, { size: 9.5, color: SUB, valign: h > 7 ? "top" : "middle", group: g }));
    els.push(F(lb, hx + 20, hy, hw - 20, h, b, { size: 9.5, color: INK, valign: h > 7 ? "top" : "middle", group: g }));
    els.push(LN(`${lb}の下線`, hx, hy + h, hw, { lw: PX1, color: "#BBBBBB", group: g }));
    hy = r1(hy + h);
  });
  // 明細表：右の列（発行者の下端 86.3）と左の列のうち低い方の下から 6mm（本物の紙の .top の下 4mm＋表の上 2mm）
  const tableY = r1(Math.max(86.3, hy) + 6);
  const bottom = (L ? 210 : 297) - 15;
  // 明細表の下に付く部品の高さ（dy の最大）：合計の3行＋内訳＋備考の枠
  const rowH = r1(look.pitch + 0.1);
  const totH = r1(rowH + 0.8);
  const sumsH = r1(rowH * 2 + totH);
  // 備考の枠：縦は内訳の下に全幅（3行）、横は高さが無いので内訳の右に並べる（そのぶん明細の行が増える）
  const notesH = kind === "receipt" ? 0 : L ? 18 : 21;
  const taxY = r1(-3 + sumsH + 1.2);
  const notesY = L ? taxY : r1(taxY + 9);
  const notesX = L ? x0Of(orient) + 140 : x0Of(orient);
  const notesW = L ? W - 140 : W;
  const dockH = kind === "receipt" ? taxY + 8 : notesY + notesH;
  const headH = r1(look.pitch + 0.1);
  const rows = Math.max(3, Math.min(kind === "receipt" ? 15 : 40, Math.floor((bottom - tableY - headH - 3 - dockH) / look.pitch)));
  const table = realTable(orient, look, tableY, rows, labels);
  // 小計／消費税／合計（税込）：明細表の下端にぴったり付ける（dy -3＝表の下端）。表の幅の枠と線で、表の中の行に見える
  const x0 = table.x;
  const amt = table.cols.find((c) => c.id === "amount")!;
  const ax = x0 + amt.x;
  const lbX = x0 + 8;
  const lbW = r1(ax - 2 - lbX);
  const tl = look.table;
  if (tl.totalFill) els.push(BX("合計の地色", x0, 0, W, totH, { dock: "table", dy: r1(-3 + rowH * 2), style: "none", fill: tl.totalFill, group: "sum" }));
  els.push(BX("合計欄（明細の下）の枠", x0, 0, W, sumsH, { dock: "table", dy: -3, style: tl.outer.style === "double" ? "double" : "solid", lw: tl.outer.w, color: tl.outer.color, fill: "", group: "sum" }));
  els.push(LN("小計の上の線", x0, 0, W, { dock: "table", dy: -3, lw: tl.sumTop.w, color: tl.sumTop.color, group: "sum" }));
  els.push(LN("消費税の上の線", x0, 0, W, { dock: "table", dy: r1(-3 + rowH), style: tl.h.style === "none" ? "solid" : tl.h.style, lw: tl.h.w, color: tl.h.color, group: "sum" }));
  els.push(LN("合計の上の線", x0, 0, W, { dock: "table", dy: r1(-3 + rowH * 2), lw: tl.sumTop.w, color: tl.sumTop.color, group: "sum" }));
  if (tl.v.style !== "none") {
    els.push(LN("合計欄の縦線（金額の左）", ax, 0, 0, { dir: "v", h: sumsH, dock: "table", dy: -3, lw: tl.v.w, color: tl.v.color, group: "sum" }));
    els.push(LN("合計欄の縦線（金額の右）", ax + amt.w, 0, 0, { dir: "v", h: sumsH, dock: "table", dy: -3, lw: tl.v.w, color: tl.v.color, group: "sum" }));
  }
  const fs = look.numLarge ? 10 : 9;
  const sums: Array<[string, string, PaperBind, number, number, boolean]> = [
    ["小計", "小計", "subtotal", -3, rowH, false],
    ["消費税", "消費税", "tax", r1(-3 + rowH), rowH, false],
    ["合計", "合計（税込）", "total", r1(-3 + rowH * 2), totH, true],
  ];
  for (const [nm, lb, b, dy, h, bold] of sums) {
    els.push(T(`${nm}（明細の下）`, lbX, 0, lbW, h, lb, { dock: "table", dy, size: bold ? 10.5 : fs, bold, align: "right", color: INK, group: "sum" }));
    els.push(F(`${nm}の金額`, ax, 0, amt.w, h, b, { dock: "table", dy, size: bold ? 10.5 : look.numLarge ? 10.5 : fs, bold, align: "right", color: INK, group: "sum" }));
  }
  els.push(F("税率ごとの内訳", x0, 0, L ? 135 : 130, L ? 14 : 8, "taxByRate", { dock: "table", dy: taxY, size: 8, color: SUB, valign: "top", locked: kind !== "quote" }));
  if (kind !== "receipt") {
    // 備考の枠（本物の紙の .notes：1px の枠・太字の「備考」）
    els.push(BX("備考の枠", notesX, 0, notesW, notesH, { dock: "table", dy: notesY, style: "solid", lw: PX1, color: INK, fill: "", group: "notes" }));
    els.push(T("備考の見出し", notesX + 2.5, 0, 30, 5, "備考", { dock: "table", dy: r1(notesY + 1.2), size: 9, bold: true, color: INK, group: "notes" }));
    els.push(F("備考", notesX + 2.5, 0, notesW - 5, notesH - 7, "notes", { dock: "table", dy: r1(notesY + 6.2), size: 9, valign: "top", color: INK, group: "notes" }));
  }
  // ページ番号（本物の紙は下の余白の右に「1 / 2」）
  els.push(F("ページ番号", (L ? 297 : 210) - 15 - 45, (L ? 210 : 297) - 11.5, 45, 4.5, "pageNo", { size: 6.5, align: "right", color: "#555555" }));
  return { els, table };
}

export const OWN_PRESET_NAMES: Record<PaperPresetKey, string> = {
  "std-p": "標準（A4縦）", "simple-p": "シンプル（A4縦）", "classic-p": "クラシック（A4縦）", "box-p": "ボックス（A4縦）",
  "breakdown-l": "標準（A4横 表紙＋内訳）", "breakdown-l2": "標準（A4横 表紙＋内訳(2)）", "works-l": "数字大きめ（A4横）", "box-l": "ボックス（A4横）",
  "combo-p": "兼請書（A4縦）", "std-l": "標準（A4横）",
};

/**
 * 明細表の列の幅だけを標準から変えるプリセット（#221、2026-10-20 射場さん）。
 *   標準（A4横 表紙＋内訳(2)）＝名称と規格を合わせた幅を2等分（No.・数量・単位・単価・金額・備考の幅と位置は標準と同じ）。
 *   色・罫線・行の高さ・テーマは元のプリセットのまま（contract の theme も同じ）
 */
const EVEN_NAME_SPEC: ReadonlySet<PaperPresetKey> = new Set<PaperPresetKey>(["breakdown-l2"]);

/** プリセットの紙の向き（contract の PAPER_PRESETS の orient。key の末尾では決めない＝breakdown-l2 も横） */
const presetOrient = (preset: PaperPresetKey): PaperOrient => paperPreset(preset)?.orient ?? (preset.endsWith("-l") ? "landscape" : "portrait");

/**
 * プリセットの書式を起こす。名前は省けば「標準（A4縦）の書式」。labels は紙に刷る見出しの言葉（会社の言葉。#142 段6 No.32）。省けば現場力の言葉。
 * 見積書・請求書は #217 の形（stdLayout＋テーマ）、領収書は #216 の形（realLayout。前の描き手）
 */
export function layoutFromPreset(kind: PaperKind, preset: PaperPresetKey, name?: string, labels?: HeadLabels): PaperLayout {
  seq = 0;
  const [w, h] = PAPER_SIZES.A4;
  if (kind === "receipt") {
    const real = realLayout("receipt", "portrait", lookOf(STD), labels ?? DEFAULT_LABELS);
    return PaperLayout.parse({
      v: 2 as const, kind, name: name ?? `${OWN_PRESET_NAMES[preset]}の書式`, orient: "portrait",
      page: { size: "A4", w, h, margin: [15, 15, 15, 15], offset: [0, 0], preprinted: false, secondPage: "compact" },
      els: real.els, table: real.table, basedOn: preset,
    });
  }
  const orient = presetOrient(preset);
  const theme = paperPreset(preset)?.theme ?? STD;
  // {order}（#233 2d）：兼請書のプリセットから起こすと 請書の欄を{order}の紙にも刷る（combined）
  const combined = kind === "order" && preset === "combo-p";
  const L = stdLayout(kind, orient, look2(theme, orient, { evenNameSpec: EVEN_NAME_SPEC.has(preset) }), labels ?? DEFAULT_LABELS, { combined });
  return PaperLayout.parse({
    v: 2 as const,
    kind,
    name: name ?? `${OWN_PRESET_NAMES[preset]}の書式`,
    orient,
    page: { size: "A4", w: orient === "portrait" ? w : h, h: orient === "portrait" ? h : w, margin: [14, 15, 16, 15], offset: [0, 0], preprinted: false, secondPage: "compact" },
    els: L.els,
    // #302（C2）：見積書・請求書のプリセットは 見出し行を No〜備考の1つの枠・文章の行を No〜名称の枠に（自分の書式は付けない限り今までの形）
    table: kind === "quote" || kind === "invoice" ? { ...L.table, spanRows: true } : L.table,
    basedOn: preset,
    ...(combined ? { combined: true } : {}),
  });
}

/**
 * #216 の複製元の形（#217 より前に保存された自分の書式の形の見本）。前の描き手（版 1）の受け入れと、
 * 前の形の書式を新しい描き手で描いたときの確かめに使う（画面からは使わない）
 */
export function legacyLayoutFromPreset(kind: PaperKind, preset: PaperPresetKey): PaperLayout {
  seq = 0;
  const orient: PaperOrient = kind !== "receipt" ? presetOrient(preset) : "portrait";
  const [w, h] = PAPER_SIZES.A4;
  const theme = kind === "receipt" ? STD : (paperPreset(preset)?.theme ?? STD);
  const real = realLayout(kind, orient, lookOf(theme), DEFAULT_LABELS);
  return PaperLayout.parse({
    v: 2 as const, kind, name: `${OWN_PRESET_NAMES[preset]}の書式`, orient,
    page: { size: "A4", w: orient === "portrait" ? w : h, h: orient === "portrait" ? h : w, margin: [15, 15, 15, 15], offset: [0, 0], preprinted: false, secondPage: "compact" },
    els: real.els, table: real.table, basedOn: preset,
  });
}

// ---- #217：今の紙（流し込み型）と同じ形のプリセット ----
/** 流し込み型の紙の線の太さ（mm）。1px＝0.26・1.5px＝0.4・2px＝0.53・3px の二重＝0.79・0.4pt＝0.14・0.6pt＝0.21 */
const K1 = 0.26, K15 = 0.4, K2 = 0.53, KD = 0.79, KP4 = 0.14, KP6 = 0.21;
const INK2 = "#1A1A1A";
const PTMM = 0.3528;
const lhMm = (size: number, lh: number): number => size * lh * PTMM;

type Rule2 = { style: "none" | "solid" | "double"; w: number; color: string };
/** テーマ → 線・色・地色・行の高さ・文字の大きさ（流し込み型の themeCss と同じ差分） */
type Look2 = {
  titleFont: "serif" | "sans"; titleLs: number; titleColor: string; titleRule: Rule2 | null;
  accent: string;
  toRule: Rule2;
  amount: { mode: "rule" | "fill" | "double"; fill: string; rule: string; labelWeight: 400 | 600; pad: number };
  issuerRule: Rule2;
  table: {
    outer: Rule2; head: Rule2; h: Rule2; v: Rule2; foot: Rule2; secRule: Rule2;
    headFill: string; zebra: string; totalFill: string; groupFill: string; secFill: string; subFill: string;
    size: number; numSize: number; pitch: number; headH: number;
    widths: Array<[PaperTable["cols"][number]["id"], number]>;
  };
};

function look2(theme: PaperTheme, orient: PaperOrient, opts: { evenNameSpec?: boolean } = {}): Look2 {
  const t = { ...STD, ...theme };
  const stdRule = t.rule === STD.rule;
  const rule = stdRule ? INK2 : up(t.rule);
  const accent = t.accent === STD.accent ? INK2 : up(t.accent);
  const simple = !t.vlines;
  const grid = t.gridColor ? up(t.gridColor) : "";
  const size = t.numLarge ? 10 : 9;
  const pitch = Math.round((lhMm(size, 1.45) + 2 * t.rowPad + K1) * 100) / 100;
  const L = orient === "landscape";
  // 列の幅（流し込み型の紙の th を測った mm。No. は 8.82＝8mm＋線）。名称は残り
  const fixed: Array<[PaperTable["cols"][number]["id"], number]> = t.numLarge
    ? [["no", 8.82], ["spec", 36], ["qty", 16], ["unit", 10], ["price", 20], ["amount", 30], ["notes", 30]]
    : [["no", 8.82], ["spec", L ? 45 : 35], ["qty", 12], ["unit", 10], ["price", 15], ["amount", 24], ["notes", L ? 55.12 : 22.12]];
  const cw = L ? 267 : 180;
  const nameW = Math.round((cw - fixed.reduce((a, [, w]) => a + w, 0)) * 100) / 100;
  const order: Array<PaperTable["cols"][number]["id"]> = ["no", "name", "spec", "qty", "unit", "price", "amount", "notes"];
  const widths = order.map((id) => [id, id === "name" ? nameW : fixed.find(([k]) => k === id)![1]] as [PaperTable["cols"][number]["id"], number]);
  if (opts.evenNameSpec) {
    // 名称と規格を合わせた幅を2等分（#221）。合わせた幅は変えない＝数量から右の列の位置は標準と同じ
    const both = nameW + fixed.find(([k]) => k === "spec")![1];
    const half = Math.round((both / 2) * 100) / 100;
    for (const w of widths) {
      if (w[0] === "name") w[1] = half;
      else if (w[0] === "spec") w[1] = Math.round((both - half) * 100) / 100;
    }
  }
  const headFill = up(t.headFill);
  const zebra = up(t.zebra);
  return {
    titleFont: t.titleFont,
    titleLs: t.titleFont === "sans" ? 0.3 : 0.5,
    titleColor: accent,
    titleRule: t.titleBorder === "none" ? null : t.titleBorder === "solid" ? { style: "solid", w: K15, color: rule } : { style: "double", w: KD, color: rule },
    accent: t.amountColor ? up(t.amountColor) : accent,
    toRule: { style: "solid", w: simple ? K1 : K15, color: rule },
    amount: {
      mode: simple ? "fill" : t.amountBorder === "double" ? "double" : "rule",
      fill: simple ? up(t.zebra || "#F4F3EF") : t.amountFill ? up(t.amountFill) : "",
      rule,
      labelWeight: t.amountColor ? 600 : 400,
      pad: simple || t.amountBorder === "double" ? 3 : 1,
    },
    issuerRule: simple ? { style: "solid", w: K15, color: rule } : { style: "double", w: KD, color: rule },
    table: {
      outer: simple ? { style: "solid", w: KP6, color: rule } : t.titleBorder === "double" && !stdRule ? { style: "double", w: KD, color: rule } : { style: "solid", w: K1, color: rule },
      head: { style: "solid", w: simple ? K15 : K2, color: simple ? INK2 : rule },
      h: simple ? { style: "solid", w: KP4, color: "#C8C5BC" } : { style: "solid", w: K1, color: grid || INK2 },
      v: simple ? { style: "none", w: KP4, color: "#C8C5BC" } : { style: "solid", w: KP4, color: grid || "#8A8A8A" },
      foot: { style: "solid", w: simple ? K15 : K2, color: simple ? INK2 : rule },
      secRule: t.titleBorder === "none" ? { style: "solid", w: K15, color: rule } : { style: "double", w: KD, color: rule },
      headFill,
      zebra,
      totalFill: t.headFill === STD.headFill ? "#F0F0F0" : headFill || zebra || "#F4F3EF",
      groupFill: t.headFill === STD.headFill ? "#F0F0F0" : headFill || zebra || "#F4F3EF",
      secFill: "#E2E2E2",
      subFill: "#FAFAFA",
      size,
      numSize: t.numLarge ? 10.5 : 9,
      pitch,
      headH: Math.round((pitch + 0.13) * 100) / 100,
      widths,
    },
  };
}

/** 項目に詰める組の印を付ける */
const inStack = (e: Extra, stack: string): Extra => ({ ...e, stack });

/**
 * 今の紙と同じ形（#217）。位置は流し込み型の紙を Chromium で描いて測った mm（docs/31 §15）。
 *   縦：左の列＝宛名・挨拶文・金額の枠・頭の欄、右＝番号・日付・ロゴ・発行者欄。横：左＝宛名・挨拶文・金額の枠、中＝頭の欄、右＝番号〜発行者欄
 *   左の列の幅は 縦 100mm・横 93.3mm（横は Q-0006 の ¥199,798,500 の紙で今の紙が広げた幅）
 */
function stdLayout(kind: "quote" | "invoice" | "order", orient: PaperOrient, look: Look2, labels: HeadLabels, opts: { combined?: boolean } = {}): { els: Extra[]; table: PaperTable } {
  const Lnd = orient === "landscape";
  const PW = Lnd ? 297 : 210;
  const PH = Lnd ? 210 : 297;
  const CW = PW - 30;
  const RX = PW - 15 - 72; // 右の列（番号・発行者欄）の左
  const leftW = Lnd ? 93.3 : 100;
  const els: Extra[] = [];
  const isQ = kind === "quote";
  const isO = kind === "order";
  // 題字と二重線（h1：22pt・字間 0.5em・下に余白 2mm と 3px の二重線）。{order}は項目（{order}・{orderAck}で変わる＝会社の言葉）
  if (isO) els.push(F("題", 15, 14, CW, 11.25, "title", { font: look.titleFont, size: 22, weight: 600, bold: true, ls: look.titleLs, lh: 1.45, align: "center", valign: "top", color: look.titleColor, locked: true }));
  else els.push(T("題字", 15, 14, CW, 11.25, isQ ? "御見積書" : "御請求書", { font: look.titleFont, size: 22, weight: 600, bold: true, ls: look.titleLs, lh: 1.45, align: "center", valign: "top", color: look.titleColor, part: "title", locked: true }));
  if (look.titleRule) els.push(LN("題字の下線", 15, 14 + 11.25 + 2 + look.titleRule.w / 2, CW, { style: look.titleRule.style, lw: look.titleRule.w, color: look.titleRule.color }));
  const top = 34.04;
  // 左：宛名（14pt・宛名の担当者 12pt・敬称 11pt）と下線
  const toW = leftW - 1;
  els.push(inStack(F("宛名", 15.5, top, toW, 7.16, "toName", { size: 14, weight: 600, bold: true, lh: 1.45, valign: "top", color: INK2, hon: { size: 11, gap: 2 }, locked: true, group: "to" }), "L"));
  els.push(inStack(F("宛名の担当者", 15.5, top + 7.16, toW, 7.16, "toSub", { size: 12, weight: 600, bold: true, lh: 1.692, valign: "top", color: INK2, hon: { size: 11, gap: 2 }, group: "to" }), "L"));
  const ul = top + 14.32 + 1 + look.toRule.w / 2;
  els.push(inStack(LN("宛名の下線", 15, ul, 80, { lw: look.toRule.w, color: look.toRule.color, group: "to" }), "L"));
  const gy = ul + look.toRule.w / 2 + 3;
  if (isO) els.push(inStack(F("挨拶文", 14.5, gy, leftW + 1, 4.86, "greet", { size: 9.5, lh: 1.45, valign: "top", color: INK2 }), "L"));
  else els.push(inStack(T("挨拶文", 14.5, gy, leftW + 1, 4.86, isQ ? "下記のとおり御見積り申し上げます。" : "下記のとおり御請求申し上げます。", { size: 9.5, lh: 1.45, valign: "top", color: INK2 }), "L"));
  // 金額の枠（上下の線・地色・二重の囲み）
  const ay = gy + 4.86 + 3;
  const ah = 12.73;
  const a = look.amount;
  if (a.mode === "rule") {
    els.push(inStack(LN("金額の枠の上の線", 15, ay + K15 / 2, leftW, { lw: K15, color: a.rule, group: "grand" }), "L"));
    els.push(inStack(LN("金額の枠の下の線", 15, ay + ah - K15 / 2, leftW, { lw: K15, color: a.rule, group: "grand" }), "L"));
    if (a.fill) els.push(inStack(BX("合計欄の枠", 15, ay, leftW, ah, { style: "none", fill: a.fill, group: "grand" }), "L"));
  } else {
    els.push(inStack(BX("合計欄の枠", 15, ay, leftW, ah, a.mode === "double" ? { style: "double", lw: KD, color: a.rule, fill: a.fill, group: "grand" } : { style: "none", fill: a.fill, group: "grand" }), "L"));
  }
  const lx = 15 + a.pad;
  const amountLabel = { size: 10, ls: 0.2, weight: a.labelWeight, bold: a.labelWeight > 400, lh: 1.45, valign: "top", color: look.accent, group: "grand" };
  if (isO) els.push(inStack(F("合計欄の見出し", lx - 0.5, ay + 4.94, 25, 5.11, "total", { ...amountLabel, head: true }), "L"));
  else els.push(inStack(T("合計欄の見出し", lx - 0.5, ay + 4.94, 25, 5.11, isQ ? "御見積金額" : "御請求金額", amountLabel), "L"));
  const tx = 15 + leftW - a.pad - 12; // （税込）の左
  els.push(inStack(F("合計（税込）", lx + 25.16 - 0.5, ay + 1.77, tx - 4 - (lx + 25.16) + 1, 9.21, "total", { font: "serif", size: 18, weight: 600, bold: true, lh: 1.45, align: "right", valign: "top", color: look.accent, prefix: "¥", suffix: "－", fit: true, locked: true, group: "grand" }), "L"));
  els.push(inStack(T("（税込）", tx - 0.5, ay + 5.47, 13, 4.35, "（税込）", { size: 8.5, lh: 1.45, valign: "top", color: SUB, group: "grand" }), "L"));
  // 頭の欄（縦は金額の枠の下、横は金額の枠の右の列）。空でも見出しと罫線を出す
  const heads: Array<[string, PaperBind]> = isO
    ? // {order}：条件の表（取適法の明示の事項・建設業法 19条）。見出しは項目の head（工事・仕入で言葉が変わる）
      [["件名", "projectName"], ["納品場所", "place"], ["受領期日", "receive"], ["検査完了期日", "inspect"], ["支払期日", "payOn"], ["支払方法", "payMethod"]]
    : isQ
      ? [[labels.subject, "projectName"], [labels.site, "site"], [labels.period, "period"], [labels.outline ?? DEFAULT_LABELS.outline, "outline"], ["有効期限", "valid"], ["担当", "staff"]]
      : [["件名", "projectName"], [labels.site, "site"], [labels.period, "period"], ["お支払期限", "due"], ["振込先", "bank"]];
  const hx = Lnd ? 15 + leftW + 8 : 15;
  const hw = Lnd ? RX - 8 - hx : leftW;
  let hy = Lnd ? top : ay + ah + 3;
  const hs = Lnd ? "H" : "L";
  heads.forEach(([lb, b], i) => {
    const g = `pj${i}`;
    if (isO) els.push(inStack(F(`${lb}の見出し`, hx + 0.5, hy, 23, 6.72, b, { size: 9.5, weight: 500, lh: 1.45, color: "#444444", group: g, head: true }), hs));
    else els.push(inStack(T(`${lb}の見出し`, hx + 0.5, hy, 19, 6.72, lb, { size: 9.5, weight: 500, lh: 1.45, color: "#444444", group: g }), hs));
    els.push(inStack(F(lb, hx + (isO ? 24.5 : 20.5), hy, hw - (isO ? 25 : 21), 6.72, b, { size: 9.5, lh: 1.45, color: INK2, group: g }), hs));
    els.push(inStack(LN(`${lb}の下線`, hx, hy + 6.72 - K1 / 2, hw, { lw: K1, color: "#BBBBBB", group: g }), hs));
    hy = Math.round((hy + 6.72) * 100) / 100;
  });
  // 右：番号・日付（右寄せ）・ロゴ・発行者欄（左に二重線・社印を会社名に重ねる）。番号・日付は2ページ目以降にも出す（右上に寄せる。2026-10-19 射場さんの決定）
  els.push(inStack(F(isO ? "番号" : isQ ? "見積番号" : "請求番号", RX, top, 72.5, 6.14, "docNo", { font: "serif", size: 12, weight: 600, bold: true, lh: 1.45, align: "right", valign: "top", color: INK2, prefix: "No. ", prefixSize: 10, prefixFont: "sans", group: "docno", locked: true, repeat: true }), "R"));
  els.push(inStack(F(isO ? "発行日" : isQ ? "見積日" : "請求日", RX, top + 6.13, 72.5, 4.6, "docDate", { size: 9, lh: 1.45, align: "right", valign: "top", color: INK2, group: "docdate", locked: true, repeat: true }), "R"));
  const logoY = top + 10.74 + 3;
  els.push(inStack(base("image", "会社のロゴ", RX, logoY, 48, 12, false, { asset: "company:logo", opacity: 1, keep: true, guide: false }), "R"));
  const iy = logoY + 12 + 1.5;
  const ix = RX + 3.8 - 0.5;
  const iw = 72 - 3.8 + 0.5;
  els.push(inStack(F("自社・会社名", ix, iy, iw, 6.35, "coName", { size: 12, weight: 600, bold: true, lh: 1.5, valign: "top", color: INK2, locked: true, group: "co" }), "R"));
  els.push(inStack(F("自社・代表者名", ix, iy + 7.35, iw, 4.76, "coRep", { size: 9, lh: 1.5, valign: "top", color: INK2, group: "co" }), "R"));
  els.push(inStack(F("自社・住所", ix, iy + 12.11, iw, 14.29, "coAddr", { size: 9, lh: 1.5, valign: "top", color: INK2, group: "co" }), "R"));
  els.push(inStack(F("自社・電話", ix, iy + 26.4, iw, 4.76, "coTel", { size: 9, lh: 1.5, valign: "top", color: INK2, group: "co" }), "R"));
  els.push(inStack(F("自社・登録番号", ix, iy + 31.16, iw, 4.76, "coReg", { size: 9, lh: 1.5, valign: "top", color: INK2, locked: kind !== "quote", group: "co" }), "R"));
  const ih = 35.92;
  els.push(inStack(LN("自社の左の線", RX + look.issuerRule.w / 2, iy, 0, { dir: "v", h: ih, style: look.issuerRule.style, lw: look.issuerRule.w, color: look.issuerRule.color, group: "co" }), "R"));
  els.push(inStack(base("image", "社印", RX + 72 - 17, iy - 2.5, 17, 17, false, { asset: "company:seal", opacity: 1, keep: true, guide: false, overlap: true }), "R"));
  // 明細表：3つの列のうちいちばん下から 4mm（今の紙の .top の下の余白）
  const bottomL = Lnd ? ay + ah : hy;
  const bottomR = iy + ih;
  const tableY = Math.round((Math.max(bottomL, Lnd ? hy : 0, bottomR) + 4) * 100) / 100;
  // ページの下：帳票名（左）とページ番号（右）。2ページ目からも同じ場所
  els.push(F("帳票名（ページの下）", 15, PH - 9.7, 120, 3, "docName", { size: 6, lh: 1.2, color: "#555555", repeat: true }));
  els.push(F("ページ番号", PW - 15 - 40, PH - 9.7, 40, 3, "pageNo", { size: 6, lh: 1.2, align: "right", color: "#555555", repeat: true }));
  const tl = look.table;
  let cx = 0;
  const lbl = colLabels(labels);
  const num = (id: string) => ["qty", "price", "amount"].includes(id);
  const cols = tl.widths.map(([id, w]) => {
    const c = { id, label: lbl[id], on: true, x: Math.round(cx * 100) / 100, w, size: id === "notes" ? tl.size - 0.5 : num(id) ? tl.numSize : tl.size, align: id === "no" ? "center" : num(id) ? "right" : "left", labelAlign: "center" };
    cx += w;
    return c;
  });
  const table = PaperTable.parse({
    x: 15, y: tableY, w: CW,
    header: { on: true, h: tl.headH, size: tl.size, bold: false, fill: tl.headFill, color: INK2 },
    rows: 12, pitch: tl.pitch, size: tl.size, color: INK2, zebra: tl.zebra, restText: "以下余白",
    rules: { outer: tl.outer, head: tl.head, h: tl.h, v: tl.v },
    cols,
    fill: true, wrap: true, lh: 1.45, padX: 1.5,
    foot: { on: true, size: tl.size, totalSize: 10.5, totalFill: tl.totalFill, rule: tl.foot, subSize: 8.5, subColor: "#444444" },
    boxes: { on: true, gap: 4, size: 9, minH: 14, rule: { style: "solid", w: K1, color: INK2 }, color: INK2 },
    sections: { font: look.titleFont, size: 14, color: look.titleColor, rule: tl.secRule, refSize: 9, refColor: "#444444", gap: 9 },
    look: { groupFill: tl.groupFill, secFill: tl.secFill, subFill: tl.subFill, textSize: 8.5, textColor: "#333333", noColor: "#444444", notesColor: "#444444" },
  });
  if (isO) {
    // 表の下：約款・基本契約書の1行と変更協議（建設の{order}だけ値が入る。空なら刷らず、下を詰める＝engine の dockPlan）
    const dk = (e: Extra, dock: "table" | "end", dy: number): Extra => ({ ...e, dock, dy });
    els.push(dk(F("約款・基本契約書の1行", 15, 0, CW, 4.8, "clause", { size: 9, lh: 1.5, valign: "top", color: INK2 }), "table", 0.5));
    els.push(dk(F("変更協議", 15, 0, CW, 4.8, "negotiation", { size: 9, lh: 1.5, valign: "top", color: INK2 }), "table", 5.5));
    // 枠（備考）のあと：請書の欄。{order}の紙では刷らず{orderAck}の紙だけ（承諾の記録・署名欄）。
    // 兼請書は{order}の紙にも 切り取り線と請書の欄（題・宛名＝自社・番号・金額・署名欄）＝{vendor}が署名して返送
    if (opts.combined) {
      const half = CW / 2;
      els.push(dk(LN("切り取り線（左）", 15, 0, half - 16, { style: "dashed", lw: 0.26, color: "#55534C" }), "end", 2.5));
      els.push(dk(T("切り取り線の印", 15 + half - 16, 0, 32, 4, "✂ 切り取り線", { size: 7.5, align: "center", color: "#55534C" }), "end", 0.5));
      els.push(dk(LN("切り取り線（右）", 15 + half + 16, 0, half - 16, { style: "dashed", lw: 0.26, color: "#55534C" }), "end", 2.5));
      els.push(dk(F("請書の欄", 15, 0, CW, 46, "ack", { size: 9, valign: "top", color: INK2 }), "end", 7));
    } else {
      els.push(dk(F("請書の欄", 15, 0, CW, 28, "ack", { size: 9, valign: "top", color: INK2 }), "end", 0));
    }
  }
  return { els, table };
}

/** 部品の「消せない」印を、その帳票の必須パーツに合わせて付け直す（読み込んだ書式にも。DB が守りで、これは画面の鍵の印） */
export function applyLocks(layout: PaperLayout, o: { construction?: boolean } = {}): PaperLayout {
  const req = new Set(requiredPartsFor(layout.kind, o));
  for (const e of layout.els) {
    const part = partOf(e);
    e.locked = !!part && req.has(part);
  }
  return layout;
}

/** 部品の 010 のパーツ id（法定必須の検査に使う）。題字は part:"title"、項目は bind から */
export function partOf(e: PaperEl): string | undefined {
  if (e.part) return e.part;
  // 見出しを刷る項目（#233 2d）は見出しだけ＝その部品に数えない（値の項目が別にある）
  if (e.type === "field") return e.head ? undefined : PAPER_BIND_PART[e.bind];
  return undefined;
}

/**
 * DB の検査（fn_check_required_parts。010／044）に渡す blocks[]：部品ごとの {part, on}＋明細表（lineItems、常に on）。
 * 「置かない」も「非表示」と同じ扱い＝無い part は blocks に無いので、必須なら DB が止める
 */
export function blocksOf(layout: PaperLayout): Array<{ part: string; on: boolean; id: string }> {
  const out: Array<{ part: string; on: boolean; id: string }> = [{ part: "lineItems", on: true, id: "table" }];
  const seen = new Map<string, boolean>();
  // 表の中の合計（#217）：小計・消費税（税率ごと）・合計（税込）の行＝合計と税率ごとの内訳
  if (layout.table.foot?.on) { seen.set("totals", true); seen.set("taxSummary", true); }
  for (const e of layout.els) {
    const part = partOf(e);
    if (!part) continue;
    seen.set(part, (seen.get(part) ?? false) || e.on);
  }
  for (const [part, on] of seen) out.push({ part, on, id: part });
  return out;
}

/** 消せない部品が消えている／無いか（画面の警告。本当の守りは DB） */
export function missingRequired(layout: PaperLayout, o: { construction?: boolean } = {}): string[] {
  const have = new Map(blocksOf(layout).map((b) => [b.part, b.on]));
  return requiredPartsFor(layout.kind, o).filter((p) => !have.get(p));
}

export const bindLabel = (b: PaperBind, labels: Labels = DEFAULT_LABELS): string => paperBindLabel(b, labels);
